import ReactDOM from "react-dom";
import styled from "styled-components";
import { Trash, X } from "lucide-react";
import { operationType } from "../../services/types";
import { useReactFlow } from "reactflow";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootState } from "../../redux/store";
import { webURL } from "../../services/API";
import axios from "axios";
import { handleRemoveVariablesFromBlock } from "../../services/method";
import { VariablesPanel } from "./variablesPanel";
import { setProjectData } from "../../redux/reducers/project";

function Portal({ children }: { children: React.ReactNode }) {
   return ReactDOM.createPortal(children, document.body);
}

export function ExpandedBlock(
   { data, handleClose, isWarning, children, styleParent, style }:
      { data: operationType, handleClose: () => void, isWarning: boolean, children: React.ReactNode, styleParent?: React.CSSProperties, style?: React.CSSProperties }
) {
   const { getNodes, getEdges, setNodes, setEdges } = useReactFlow();
   const [searchParams] = useSearchParams();
   const projectId = searchParams.get('id');
   const userId = useSelector((state: RootState) => state.user.data?._id);
   const dispatch = useDispatch();

   // ---
   const handleRemove = async () => {
      handleRemoveVariablesFromBlock({ id: data.id as string, getNodes, getEdges, setNodes, setEdges });
      dispatch(setProjectData({ isThereBlockExpanded: false }));

      if (data.action === "aiAgent") {
         if (projectId && data.index && userId) {
            // Delete both user and admin chat threads for this user
            await axios.delete(`${webURL}/agents/aiAgentChat?thread_id=${encodeURIComponent(`${projectId}-${data.index}-user-${userId}`)}`).catch(() => {});
            await axios.delete(`${webURL}/agents/aiAgentChat?thread_id=${encodeURIComponent(`${projectId}-${data.index}-admin-${userId}`)}`).catch(() => {});
         }
      }
   }

   return (
      <>
         <Portal><VariablesPanel data={data} /></Portal>
         <Portal>
            <Main
               onMouseDown={_=> {
                  handleClose();
                  dispatch(setProjectData({ isThereBlockExpanded: false }));
               }}
            >
               <ContentWrapper>
                  <div className={isWarning ? 'warning' : ''} onMouseDown={(e) => e.stopPropagation()} style={styleParent}>
                     <div className="title" style={{ backgroundColor: data.background }}>
                        <span>{data.index}</span>
                        <div>
                           <Trash size={ 18 } onClick={handleRemove} />
                           <X 
                              className="close" 
                              size={ 21 } 
                              onClick={ _=> {
                                 handleClose();
                                 dispatch(setProjectData({ isThereBlockExpanded: false }));
                              }} 
                           />
                        </div>
                     </div>
                     <div className="children" style={style}>
                        {children}
                     </div>
                  </div>
               </ContentWrapper>
            </Main>
         </Portal>
      </>
   );
}

const ContentWrapper = styled.div`
   margin-left: 400px;
   display: flex;
   align-items: center;
   justify-content: center;
   width: calc(100% - 400px);
   height: 100vh;
`;

const Main = styled.div`
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100vh;
   display: flex;
   backdrop-filter: blur(2px);
   background: rgba(0, 0, 0, 0.1);
   z-index: 9999999;

   > ${ContentWrapper} > div {
      position: relative;

      &.warning {
         border: 1px solid #EF6C25;
         border-radius: 5px 5px 15px 15px;
      }
      > .title {
         display: flex;
         align-items: center;
         justify-content: space-between;
         height: 35px;
         border-radius: 5px 5px 0 0;
         color: #fff;
         padding: 0 15px;
         font-size: 16px;

         > div {
            display: flex;
            align-items: center;

            svg {
               transition: all .2s ease-in-out;

               &:first-of-type {
                  margin-right: 15px;
               }
               &:hover {
                  opacity: 0.75;
                  cursor: pointer;
               }
            }
         }
      }
   }
   .children {
      position: relative;
      padding: 30px;
      background-color: #fff;
      box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
      border-radius: 0 0 15px 15px;
      border: 1px solid rgb(226, 232, 240);
   }
`;